<script setup>
/**
 * Conversation attributes and People attributes.
 *
 * One page, two routes. Custom attributes used to be a single page with a tab
 * per model, which buried contact fields behind a control most people never
 * clicked and gave the navigation nothing to point at. The models are now two
 * sibling nav leaves in the Data group - People attributes and Conversation
 * attributes - and the route says which model this instance shows, so the page
 * has no tab state of its own.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

import AddAttribute from './AddAttribute.vue';
import EditAttribute from './EditAttribute.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import AttributeListItem from 'dashboard/components-next/ConversationWorkflow/AttributeListItem.vue';

const props = defineProps({
  attributeModel: {
    type: String,
    default: 'conversation_attribute',
    validator: value =>
      ['conversation_attribute', 'contact_attribute'].includes(value),
  },
});

// A conversation field and a customer field are the same record to the API and
// two different things to the person reading the page, so the copy is per model.
const PAGE_COPY = {
  conversation_attribute: {
    HEADER: 'ATTRIBUTES_MGMT.PAGE.CONVERSATION.HEADER',
    DESCRIPTION: 'ATTRIBUTES_MGMT.PAGE.CONVERSATION.DESCRIPTION',
    BUTTON: 'ATTRIBUTES_MGMT.PAGE.CONVERSATION.BUTTON',
    SEARCH_PLACEHOLDER: 'ATTRIBUTES_MGMT.PAGE.CONVERSATION.SEARCH_PLACEHOLDER',
    EMPTY: 'ATTRIBUTES_MGMT.PAGE.CONVERSATION.EMPTY',
  },
  contact_attribute: {
    HEADER: 'ATTRIBUTES_MGMT.PAGE.CONTACT.HEADER',
    DESCRIPTION: 'ATTRIBUTES_MGMT.PAGE.CONTACT.DESCRIPTION',
    BUTTON: 'ATTRIBUTES_MGMT.PAGE.CONTACT.BUTTON',
    SEARCH_PLACEHOLDER: 'ATTRIBUTES_MGMT.PAGE.CONTACT.SEARCH_PLACEHOLDER',
    EMPTY: 'ATTRIBUTES_MGMT.PAGE.CONTACT.EMPTY',
  },
};

const { t } = useI18n();

const getters = useStoreGetters();
const store = useStore();
const { currentAccount } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const [showAddPopup, toggleAddPopup] = useToggle(false);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);
const searchQuery = ref('');
const selectedAttribute = ref({});

const uiFlags = computed(() => getters['attributes/getUIFlags'].value);

const copy = computed(() => PAGE_COPY[props.attributeModel]);

// `AddAttribute` still takes the model as an index into its own model list.
const attributeModelIndex = computed(() =>
  props.attributeModel === 'contact_attribute' ? 1 : 0
);

const openAddPopup = () => {
  toggleAddPopup(true);
};
const hideAddPopup = () => {
  toggleAddPopup(false);
};
const hideEditPopup = () => {
  toggleEditPopup(false);
  selectedAttribute.value = {};
};
const closeDelete = () => {
  toggleDeletePopup(false);
  selectedAttribute.value = {};
};

onMounted(() => {
  store.dispatch('attributes/get');
});

const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value(props.attributeModel)
);

const handleEditAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleEditPopup(true);
};

const handleDeleteAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleDeletePopup(true);
};

const confirmDeleteAttribute = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    useAlert(t('ATTRIBUTES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    closeDelete();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ATTRIBUTES_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const requiredAttributeKeys = computed(
  () => currentAccount.value?.settings?.conversation_required_attributes || []
);

const hasPreChatBadge = attribute => {
  return (inboxes.value || []).some(inbox => {
    const fields =
      inbox?.pre_chat_form_options?.pre_chat_fields ||
      inbox?.channel?.pre_chat_form_options?.pre_chat_fields ||
      [];
    return fields.some(field => field.name === attribute.attribute_key);
  });
};

const buildBadges = attribute => {
  const badges = [];
  if (hasPreChatBadge(attribute)) {
    badges.push({
      type: 'pre-chat',
    });
  }

  if (
    attribute.attribute_model === 'conversation_attribute' &&
    requiredAttributeKeys.value.includes(attribute.attribute_key)
  ) {
    badges.push({
      type: 'resolution',
    });
  }

  return badges;
};

const derivedAttributes = computed(() =>
  attributes.value.map(attribute => ({
    ...attribute,
    label: attribute.attribute_display_name,
    type: attribute.attribute_display_type,
    value: attribute.attribute_key,
    badges: buildBadges(attribute),
  }))
);

const filteredAttributes = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return derivedAttributes.value;
  return picoSearch(derivedAttributes.value, query, [
    'attribute_display_name',
    'attribute_key',
    'attribute_description',
  ]);
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t(copy.HEADER)"
    :description="$t(copy.DESCRIPTION)"
  >
    <template #actions>
      <Button :label="$t(copy.BUTTON)" size="sm" @click="openAddPopup" />
    </template>

    <template #tabs>
      <div class="flex items-center justify-between gap-3">
        <SettingsSearchInput
          v-model="searchQuery"
          :placeholder="$t(copy.SEARCH_PLACEHOLDER)"
        />
        <span v-if="attributes.length" class="text-sm text-n-slate-11">
          {{ $t('ATTRIBUTES_MGMT.FIELD_COUNT', { n: attributes.length }) }}
        </span>
      </div>
    </template>

    <div
      v-if="filteredAttributes.length"
      class="flex flex-col divide-y divide-n-weak border-t border-n-weak"
    >
      <AttributeListItem
        v-for="attribute in filteredAttributes"
        :key="attribute.id"
        :attribute="attribute"
        :badges="attribute.badges"
        @edit="handleEditAttribute"
        @delete="handleDeleteAttribute"
      />
    </div>
    <p
      v-else-if="!uiFlags.isFetching"
      class="flex items-center justify-center py-20 mb-0 text-base text-n-slate-11"
    >
      {{ searchQuery ? $t('ATTRIBUTES_MGMT.NO_RESULTS') : $t(copy.EMPTY) }}
    </p>

    <AddAttribute
      v-if="showAddPopup"
      v-model:show="showAddPopup"
      :on-close="hideAddPopup"
      :selected-attribute-model-tab="attributeModelIndex"
    />
    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAttribute
        :selected-attribute="selectedAttribute"
        :is-updating="uiFlags.isUpdating"
        @on-close="hideEditPopup"
      />
    </woot-modal>
    <ConfirmDialog
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.TITLE', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      :description="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.MESSAGE')"
      :confirm-button-label="`${$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.YES')} ${
        selectedAttribute.attribute_display_name || ''
      }`"
      :cancel-button-label="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.NO')"
      :confirm-value="selectedAttribute.attribute_display_name"
      :confirm-placeholder="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      @confirm="confirmDeleteAttribute"
      @close="closeDelete"
    />
  </SettingsPageShell>
</template>
